import styles from './Event.module.css';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Fade from '@mui/material/Fade';
import Switch from '@mui/material/Switch';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import Clear from '@mui/icons-material/Clear';
import Delete from '@mui/icons-material/Delete';
import Save from '@mui/icons-material/Save';

import { DateTime } from 'luxon';

import { useState } from 'react';

import { useInterval } from '../../hooks/useInterval';
import { useAuth } from '../../AuthContext';
import { EarlyAccessCard } from './EarlyAccessCard';

const InfoTooltip = ({ description, variant, footer }) => {
    const [ anchorEl, setAnchorEl ] = useState(null);

    const handlePopoverOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    return (
        <>
            <InfoOutlinedIcon
                fontSize="small"
                onMouseEnter={ handlePopoverOpen }
                onMouseLeave={ handlePopoverClose }
                onClick={ handlePopoverOpen }
                tabIndex={ 0 } // Make it properly focusable
                onFocus={ handlePopoverOpen } // Handle keyboard focus
                onBlur={ handlePopoverClose } // Handle keyboard blur
                sx={
                    {
                        cursor: 'pointer',
                        color: variant === 'required' ? '#f36647ff' : (variant === 'warning' ? '#ed6c02' : (variant === 'info' ? '#1976d2' : 'inherit'))
                    }
                }
                aria-describedby={ open ? 'info-tooltip' : undefined } // Proper ARIA relationship
            />
            <Popover
                id="info-tooltip"
                open={ open }
                anchorEl={ anchorEl }
                sx={ { pointerEvents: 'none' } }
                anchorOrigin={ {
                    vertical: 'bottom',
                    horizontal: 'left',
                } }
                transformOrigin={ {
                    vertical: 'top',
                    horizontal: 'left',
                } }
                onClose={ handlePopoverClose }
                disableRestoreFocus
                disableScrollLock={ true }
                disableAutoFocus={ true } // Prevent auto focus
                disableEnforceFocus={ true } // Prevent focus enforcement
                aria-hidden={ false } // Don't hide from screen readers
            >
                <Typography sx={ { p: 1, maxWidth: 250 } }>{ description }</Typography>
                <Typography sx={ { p: 1, fontStyle: 'italic', display: footer ? 'block' : 'none' } }>{ footer }</Typography>
            </Popover>
        </>
    );
};

export const Event = ({ event, onSaveEvent, onDeleteEvent, onCancel, readOnly = true }) => {
    const { claims } = useAuth();
    const [ error, setError ] = useState("");
    const [ saveError, setSaveError ] = useState("");
    const [ deleteConfirm, setDeleteConfirm ] = useState(false);
    const [ updatedEvent, setUpdatedEvent ] = useState(event);
    const [ hasChanged, setHasChanged ] = useState(false);
    const [ showSuccess, setShowSuccess ] = useState(false);

    const handleOnChange = (e) => {
        if (readOnly) return; // Prevent any changes in read-only mode

        const { name, value } = e.target;

        if (name === 'earlyRewardAllShifts' || name === 'earlyRewardPerShift') {
            const checked = e.target.checked;

            if (checked) {
                // If checking this box, uncheck the other
                setUpdatedEvent({
                    ...updatedEvent,
                    earlyRewardAllShifts: name === 'earlyRewardAllShifts' ? true : false,
                    earlyRewardPerShift: name === 'earlyRewardPerShift' ? true : false,
                });
            } else {
                // If unchecking, set both to false
                setUpdatedEvent({
                    ...updatedEvent,
                    earlyRewardAllShifts: false,
                    earlyRewardPerShift: false,
                });
            }
        } else if (name === 'allowUnscheduled' || name === 'allowUnscheduledAfter') {
            const checked = e.target.checked;

            if (name === 'allowUnscheduled') {
                // When changing allowUnscheduled, also control allowUnscheduledAfter
                setUpdatedEvent({
                    ...updatedEvent,
                    allowUnscheduled: checked,
                    allowUnscheduledAfter: checked ? updatedEvent.allowUnscheduledAfter : false,
                });
            } else if (name === 'allowUnscheduledAfter') {
                // When changing allowUnscheduledAfter, only update if allowUnscheduled is enabled
                setUpdatedEvent({
                    ...updatedEvent,
                    allowUnscheduledAfter: checked && updatedEvent.allowUnscheduled,
                });
            }
        } else {
            setUpdatedEvent({ ...updatedEvent, [ name ]: value });
        }

        setHasChanged(true);
    };

    const handleOnCancel = () => {
        if (readOnly) return; // Prevent action in read-only mode

        setUpdatedEvent(event); // Reset to original
        setHasChanged(false);
        onCancel();
    };

    const handleOnSave = async () => {
        if (readOnly) return; // Prevent action in read-only mode

        if (!updatedEvent.type || updatedEvent.type.trim() === "") {
            setError("required");

            return;
        }

        try {
            await onSaveEvent({ ...updatedEvent, type: updatedEvent.type?.toLowerCase() || '' });

            setShowSuccess(true);
            setHasChanged(false);
            setError(""); // Clear any previous errors
            setSaveError(""); // Clear save error
            setTimeout(() => setShowSuccess(false), 3000);
        } catch (e) {
            setSaveError(e.message || "An error occurred while saving the event.");
        }
    };

    const handleOnDelete = () => {
        if (readOnly) return; // Prevent action in read-only mode

        if (!deleteConfirm) {
            setDeleteConfirm(true);

            return;
        }

        onDeleteEvent(updatedEvent.id);
        onCancel();
    };

    // Helper to render read-only value or editable input
    const renderField = (label, name, value, type = 'text', tooltip, required = false, isDate = false, isDateTime = false) => {
        const displayValue = isDate ? (value ? DateTime.fromISO(value).toFormat('yyyy-MM-dd') : '') :
            isDateTime ? (value ? DateTime.fromISO(value).toFormat('yyyy-MM-dd HH:mm') : '') : value;

        return (
            <div className={ styles.item }>
                <div className={ styles.labelWithIcon }>
                    <Typography sx={ { fontSize: 'inherit' } }>{ label }</Typography>
                    
                    { tooltip }
                </div>

                { readOnly ? (
                    <Typography sx={ { padding: '8px 0', fontSize: 'inherit' } }>{ displayValue || 'N/A' }</Typography>
                ) : (
                    <input
                        type={ type }
                        id={ name }
                        name={ name }
                        className={ error && required && styles.error }
                        value={ displayValue }
                        onChange={ handleOnChange }
                        required={ required }
                    />
                ) }
            </div>
        );
    };

    return (
        <div className={ styles.eventCard }>
            <div className={ styles.event }>
                { renderField("Long Name", "nameLong", updatedEvent.nameLong, 'text', <InfoTooltip description="The full, descriptive name of the event that will be displayed in detailed views." />) }
                { renderField("Short Name", "nameShort", updatedEvent.nameShort, 'text', <InfoTooltip description="A brief name or abbreviation for the event, used in compact displays and lists." />) }
                { renderField("Description", "description", updatedEvent.description, 'text', <InfoTooltip description="A brief description of the event." />) }
                { renderField("Location", "location", updatedEvent.location, 'text', <InfoTooltip description="The physical location where the event will take place (address, building, or landmark)." />) }
                { renderField("Organizer", "organizer", updatedEvent.organizer?.email, 'text', <InfoTooltip description="The person responsible for organizing and managing this event (readonly)." />, false, false, false, true) }
                { renderField("Type", "type", updatedEvent.type, 'text', <InfoTooltip variant="required" description="The category or type of event (e.g., patrol, training, meeting). This is a freeform field that is used to group roles with shifts." footer="This is a required field." />, true) }
                { renderField("Attendance", "attendance", updatedEvent.attendance, 'text', <InfoTooltip description="Required, recommended, optional. This indicates the expected level of participation for the event." />) }
                { renderField("Limit", "limit", updatedEvent.limit, 'text', <InfoTooltip description="The maximum number of role-specific shifts the patroller is allowed to sign up for. Patrollers can sign up for unlimited 'unscheduled' shifts." />) }

                <Divider sx={ { my: 2 } } />

                { renderField("Event Start Date", "start", updatedEvent.start, 'date', <InfoTooltip variant="required" description="The date when the event begins." footer="This is a required field." />, true, true) }
                { renderField("Event End Date", "end", updatedEvent.end, 'date', <InfoTooltip variant="required" description="The date when the event ends." footer="This is a required field." />, true, true) }

                <div className={ styles.item }>&nbsp;</div>

                { renderField("Early Sign-Up Open", "earlyOpen", updatedEvent.earlyOpen, 'datetime-local', <InfoTooltip description="The date and time when early sign-up for this event opens." />, false, false, true) }
                { renderField("Early Sign-Up Close", "earlyClose", updatedEvent.earlyClose, 'datetime-local', <InfoTooltip description="The date and time when early sign-up for this event closes." />, false, false, true) }

                <div className={ styles.item }>&nbsp;</div>

                { renderField("General Registration Open", "opens", updatedEvent.opens, 'datetime-local', <InfoTooltip description="The date and time when general sign-up for this event opens." />, false, false, true) }
                { renderField("General Registration Close", "closes", updatedEvent.closes, 'datetime-local', <InfoTooltip description="The date and time when general sign-up for this event closes." />, false, false, true) }

                <EarlyAccessCard eventId={ event?.id } event={ event } isAdmin={ !!claims?.admin && !readOnly } />

                <Divider sx={ { my: 2 } } />

                <div className={ styles.item }>
                    <div className={ styles.labelWithIcon }>
                        <Typography sx={ { fontSize: 'inherit' } }>Early Sign-Up Reward All Shifts</Typography>
                        <InfoTooltip description="Any shift completed in this event will be eligible for early sign-up." />
                    </div>

                    { readOnly ? (
                        <Typography sx={ { padding: '8px 0', fontSize: 'inherit' } }>{ updatedEvent.earlyRewardAllShifts ? 'Yes' : 'No' }</Typography>
                    ) : (
                        <Switch id="eventEarlyReward" name="earlyRewardAllShifts" checked={ updatedEvent.earlyRewardAllShifts || false } onChange={ handleOnChange } />
                    ) }
                </div>

                <div className={ styles.item }>
                    <div className={ styles.labelWithIcon }>
                        <Typography sx={ { fontSize: 'inherit' } }>Early Sign-Up Reward Per Shift</Typography>
                        <InfoTooltip description="Only designated shifts completed in this event will be eligible for early sign-up." />
                    </div>

                    { readOnly ? (
                        <Typography sx={ { padding: '8px 0', fontSize: 'inherit' } }>{ updatedEvent.earlyRewardPerShift ? 'Yes' : 'No' }</Typography>
                    ) : (
                        <Switch id="eventShiftEarlyReward" name="earlyRewardPerShift" checked={ updatedEvent.earlyRewardPerShift || false } onChange={ handleOnChange } />
                    ) }
                </div>

                <Divider sx={ { my: 2 } } />

                <div className={ styles.item }>
                    <div className={ styles.labelWithIcon }>
                        <Typography sx={ { fontSize: 'inherit' } }>Allow 'Unscheduled' Shifts</Typography>
                        <InfoTooltip description="Allow patrollers to sign up for 'Unscheduled' shifts at any time, regardless of slot availability." />
                    </div>

                    { readOnly ? (
                        <Typography sx={ { padding: '8px 0', fontSize: 'inherit' } }>{ updatedEvent.allowUnscheduled ? 'Yes' : 'No' }</Typography>
                    ) : (
                        <Switch id="allowUnscheduled" name="allowUnscheduled" checked={ updatedEvent.allowUnscheduled || false } onChange={ handleOnChange } />
                    ) }
                </div>

                <div className={ styles.item }>
                    <div className={ styles.labelWithIcon }>
                        <Typography sx={ { fontSize: 'inherit' } } color={ updatedEvent.allowUnscheduled ? 'textPrimary' : 'textDisabled' }>Allow 'Unscheduled' Shifts after All Roles Filled</Typography>
                        <InfoTooltip description="Allow patrollers to sign up for 'Unscheduled' shifts once all roles have been filled." />
                    </div>

                    { readOnly ? (
                        <Typography sx={ { padding: '8px 0', fontSize: 'inherit' } }>{ updatedEvent.allowUnscheduledAfter ? 'Yes' : 'No' }</Typography>
                    ) : (
                        <Switch id="allowUnscheduledAfter" name="allowUnscheduledAfter" disabled={ !updatedEvent.allowUnscheduled } checked={ updatedEvent.allowUnscheduledAfter || false } onChange={ handleOnChange } />
                    ) }
                </div>

                <Divider sx={ { my: 2 } } />

                <div className={ styles.actions } style={ { display: readOnly ? 'none' : 'flex' } }>
                    <Fade in={ showSuccess } timeout={ { enter: 100, exit: 500 } } unmountOnExit>
                        <Alert variant='filled' severity="success">Changes saved.</Alert>
                    </Fade>

                    { hasChanged && <Alert variant='filled' severity="info" onClose={ () => setError(null) }>You have unsaved changes.</Alert> }
                    { error && <Alert variant='filled' severity="error" onClose={ () => setError(null) }>{ error }</Alert> }
                    { saveError && <Alert variant='filled' severity="error" onClose={ () => setSaveError(null) }>{ saveError }</Alert> }
                    { deleteConfirm && <Alert variant='filled' severity="warning" onClose={ () => setDeleteConfirm(false) }>Click delete again to confirm.</Alert> }

                    <Stack direction="row" spacing={ 1 } justifyContent='flex-end' sx={ { width: '100%' } }>
                        <Button variant="outlined" color="error" onClick={ handleOnDelete } disabled={ !updatedEvent.id } startIcon={ <Delete /> }>delete</Button>

                        <Divider orientation="vertical" flexItem sx={ { mx: 1 } } />

                        <Button variant="outlined" color="secondary" onClick={ handleOnCancel } startIcon={ <Clear /> }>close</Button>
                        <Button variant="contained" color="primary" onClick={ handleOnSave } disabled={ !hasChanged } startIcon={ <Save /> }>save event</Button>
                    </Stack>
                </div>
            </div>
        </div>
    );
};